'use client'

import * as React from 'react'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useStoreBaseCurrencyRate } from '@/lib/api/currency-rates'
import { useStoreCurrencies } from '@/lib/api/stores'
import {
  getCurrencyOption,
  normalizeCurrencyCodes,
  normalizeCurrencyOptions,
  type CurrencyOption,
  type CurrencyOptions,
  type CurrencyOptionsInput,
} from '@/lib/currency/currencyOptionsKeys'
import { convertMinorUnits, formatMoney, minorUnitsToMajorUnits, moneyStringOrNumberToMinorUnits } from "@/lib/money"

export type { CurrencyOption }
export type CurrencyOptionsType = CurrencyOptions

interface CurrencyPriceInputProps {
    values: CurrencyOptionsInput | null
    onChange: (data: { currencyOptions: CurrencyOptionsType }) => void
    storeId: string
    disabled?: boolean
}

export const CurrencyPriceInput = ({ values, onChange, storeId, disabled = false }: CurrencyPriceInputProps) => {
    const storeCurrencies = useStoreCurrencies(storeId)
    const { data: currencyRateData } = useStoreBaseCurrencyRate(storeId || '', !!storeId)

    if (storeCurrencies.isLoading) {
        return <div className="text-xs text-muted-foreground">Loading currencies...</div>
    }

    if (storeCurrencies.isError) {
        return <div className="text-xs text-red-500">Error loading currencies</div>
    }

    const currencies = storeCurrencies.data.length > 0 ? storeCurrencies.data : []
    const baseCurrency = currencyRateData?.name?.toUpperCase() || 'USD'
    const exchangeRates = currencyRateData?.data || {}

    return (
        <CurrencyPriceInputContent
            currencies={currencies}
            baseCurrency={baseCurrency}
            exchangeRates={exchangeRates}
            values={values}
            onChange={onChange}
            disabled={disabled}
        />
    )
}

interface CurrencyPriceInputContentProps {
    currencies: string[]
    baseCurrency: string
    exchangeRates: Record<string, number>
    values: CurrencyOptionsInput | null
    onChange: (data: { currencyOptions: CurrencyOptionsType }) => void
    disabled: boolean
}

const buildOptionForCurrency = (
    values: CurrencyOptionsInput | null,
    currency: string,
    now: string,
): CurrencyOption => {
    const existing = getCurrencyOption(values, currency)?.option
    return {
        rate: existing?.rate ?? 1,
        amount: existing?.amount ?? 0,
        lock: existing?.lock ?? false,
        date: existing?.date ?? now,
        ...(existing?.lockedBy && { lockedBy: existing.lockedBy }),
    }
}

const buildCurrencyOptions = (
    currencies: string[],
    values: CurrencyOptionsInput | null,
): CurrencyOptionsType => {
    const now = new Date().toISOString()
    const codes = normalizeCurrencyCodes(currencies)
    return Object.fromEntries(codes.map((currency) => [currency, buildOptionForCurrency(values, currency, now)]))
}

const CurrencyPriceInputContent = ({
    currencies,
    baseCurrency,
    exchangeRates,
    values,
    onChange,
    disabled,
}: CurrencyPriceInputContentProps) => {
    const normalizedCurrencies = normalizeCurrencyCodes(currencies)

    const getValidCurrency = (current: string) => {
        const upper = current.toUpperCase()
        return normalizedCurrencies.includes(upper) ? upper : normalizedCurrencies[0]
    }

    const [currentCurrency, setCurrentCurrency] = React.useState(() => getValidCurrency(normalizedCurrencies[0]))

    const handleSetCurrency = (currency: string) => {
        setCurrentCurrency(getValidCurrency(currency))
    }

    const currencyOptions = React.useMemo(
        () => buildCurrencyOptions(normalizedCurrencies, values),
        [normalizedCurrencies.join(','), values],
    )

    const getCurrencyOptionsWithResyncAmounts = React.useCallback(
        (data: CurrencyOptionsType) => {
            const baseCurrencyData = getCurrencyOption(data, baseCurrency)?.option
            if (!baseCurrencyData) return data

            return Object.fromEntries(
                Object.entries(data).map(([key, value]) => {
                    if (key.toUpperCase() === baseCurrency.toUpperCase() || value.lock) {
                        return [key, value]
                    }
                    const targetCurrencyRate = exchangeRates[key.toLowerCase()] || 1
                    return [
                        key,
                        {
                            ...value,
                            rate: targetCurrencyRate,
                            amount: convertMinorUnits(baseCurrencyData.amount, baseCurrency, targetCurrencyRate, key),
                        },
                    ]
                }),
            )
        },
        [baseCurrency, exchangeRates],
    )

    const handleChangeCurrencyOption = React.useCallback(
        (currency: string, data: CurrencyOption, resyncAmounts: boolean) => {
            const updatedCurrencyOptions = {
                ...currencyOptions,
                [currency.toUpperCase()]: data,
            }
            const next = resyncAmounts
                ? getCurrencyOptionsWithResyncAmounts(updatedCurrencyOptions)
                : updatedCurrencyOptions
            onChange({ currencyOptions: normalizeCurrencyOptions(next) })
        },
        [currencyOptions, getCurrencyOptionsWithResyncAmounts, onChange],
    )

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
                {normalizedCurrencies.map((currency) => (
                    <Badge
                        key={currency}
                        variant={currentCurrency === currency ? 'default' : 'outline'}
                        onClick={() => handleSetCurrency(currency)}
                        className="cursor-pointer text-xs"
                    >
                        {formatMoney(currencyOptions[currency].amount, currency)}
                    </Badge>
                ))}
            </div>

            {Object.keys(currencyOptions).map((item) => (
                <CurrencyField
                    key={item}
                    visible={currentCurrency === item}
                    currency={item}
                    currencyOption={currencyOptions[item]}
                    onChange={(data, resyncAmounts) => handleChangeCurrencyOption(item, data, resyncAmounts)}
                    isBaseCurrency={item === baseCurrency}
                    disabled={disabled}
                />
            ))}
        </div>
    )
}

const CurrencyField = React.memo(
    ({
        currencyOption,
        currency,
        visible,
        onChange,
        isBaseCurrency,
        disabled,
    }: {
        currencyOption: CurrencyOption
        currency: string
        visible: boolean
        onChange: (data: CurrencyOption, resyncAmounts: boolean) => void
        isBaseCurrency: boolean
        disabled: boolean
    }) => {
        const displayValue = minorUnitsToMajorUnits(currencyOption.amount, currency)

        const handleChange = (value: string) => {
            const amountInMinorUnits = moneyStringOrNumberToMinorUnits(value || '0', currency)
            const updatedData = {
                ...currencyOption,
                amount: amountInMinorUnits,
                lock: true,
                lockedBy: 'merchant' as const,
                date: new Date().toISOString(),
                rate: 1,
            }
            const needToResyncAmounts = isBaseCurrency
            onChange(updatedData, needToResyncAmounts)
        }

        const handleResetLock = () => {
            const { lockedBy: _lockedBy, ...unlocked } = currencyOption
            const updatedData = {
                ...unlocked,
                lock: false,
                date: new Date().toISOString(),
            }
            onChange(updatedData, true)
        }

        return (
            <div className="flex items-center gap-2" style={{ display: visible ? 'flex' : 'none' }}>
                <div className="w-14">
                    <Badge variant="outline" className="w-full justify-center py-1 text-xs">
                        {currency}
                    </Badge>
                </div>
                <div className="relative h-7 flex-1">
                    <Input
                        id={`${currency}-amount`}
                        type="number"
                        step="0.01"
                        value={displayValue || ''}
                        onChange={(e) => handleChange(e.target.value)}
                        className="h-full text-xs"
                        placeholder="0.00"
                        disabled={disabled}
                    />
                    {!disabled && currencyOption.lock && !isBaseCurrency && (
                        <div className="absolute inset-y-0 right-2 flex items-center">
                            <Badge
                                variant="secondary"
                                className="cursor-pointer px-2 py-0.5 text-[10px]"
                                onClick={handleResetLock}
                            >
                                Unlock
                            </Badge>
                        </div>
                    )}
                </div>
            </div>
        )
    },
)

CurrencyField.displayName = 'CurrencyField'
